import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import usePageTracker from "@/hooks/usePageTracker";
import { Button } from "@/components/ui/button";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import getApiUrl from "@/utils/api";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";
import { CalendarIcon, ArrowLeft, ArrowRight, Plane, MapPin, User, Phone, Home, Luggage, CreditCard, Check, Users, Package, Clock, Search, Loader2, RotateCcw, Ticket, AlertCircle, Upload, Image, ArrowRightCircle, MessageCircle, Heart, DollarSign } from "lucide-react";
import { getAirlineColors, getAirlineCode, getFlightStatus } from "@/utils/airlineUtils";

import { PricingGuidelines } from "@/components/booking/PricingGuidelines";
import { BookingSummary } from "@/components/booking/BookingSummary";
import { BookingStepProgress } from "@/components/booking/BookingStepProgress";
import { BookingNavigation } from "@/components/booking/BookingNavigation";

// Error arrow indicator component
const ErrorArrow = ({ show }) => {
  if (!show) return null;
  return (
    <span className="inline-flex items-center ml-2 text-red-500 animate-pulse">
      <ArrowRightCircle className="w-5 h-5" />
    </span>
  );
};

import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { PhoneInput } from "@/components/PhoneInput";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useLanguage } from "@/i18n/LanguageContext";

const API = getApiUrl();

// Required field label component
const RequiredLabel = ({ children, className = "" }) => (
  <Label className={`text-sm font-medium text-gray-600 mb-2 block ${className}`}>
    {children}
    <span className="text-red-500 ml-1">*</span>
  </Label>
);

// CSS class for required input fields with red highlight
const requiredInputClass = "h-12 rounded-xl bg-white border-[3px] border-red-500 focus:border-red-600 focus:ring-2 focus:ring-red-200";

// Optional field label component  
const OptionalLabel = ({ children, className = "" }) => (
  <Label className={`text-sm font-medium text-gray-600 mb-2 block ${className}`}>
    {children}
    <span className="text-gray-400 ml-1 text-xs">(optional)</span>
  </Label>
);

export default function BookingPage() {
  usePageTracker("Book Trip");
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [airports, setAirports] = useState([]);
  const [housingUnits, setHousingUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [flightLoading, setFlightLoading] = useState(false);
  const [flightInfo, setFlightInfo] = useState(null);
  const [date, setDate] = useState(null);
  const [useCustomAddress, setUseCustomAddress] = useState(false);
  
  // Return trip customer type
  const [customerType, setCustomerType] = useState(null); // "first_time" or "returning"
  const [confirmationNumber, setConfirmationNumber] = useState("");
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const [previousBooking, setPreviousBooking] = useState(null);
  
  const [formData, setFormData] = useState({
    full_name: "",
    whatsapp_number: "",
    email: "",
    airline: "",
    flight_number: "",
    arrival_terminal: "",
    housing_unit: "",
    housing_address: "",
    housing_phone: "",
    trip_type: "arrival",
    pickup_location: "",
    dropoff_location: "",
    travel_date: "",
    travel_time: "",
    num_passengers: 1,
    total_suitcases: null,
    total_carry_ons: 0,
    extra_suitcases: 0,
    uses_shipping_company: false,
    ride_type: "shared",
    payment_method: "stripe",
    pay_full_trip: false,
    tip_amount: 0,
    tip_percent: 0,
    referral_code: "",
    departure_time: "",
    redeem_credits: false
  });
  const [availableCredits, setAvailableCredits] = useState(0);

  // Passenger details for each traveler
  const [passengers, setPassengers] = useState([
    { name: "", whatsapp: "", destination: "", custom_address: "" }
  ]);
  const [sameDestination, setSameDestination] = useState(true);

  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [paymentScreenshotPreview, setPaymentScreenshotPreview] = useState(null);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const [pricing, setPricing] = useState({
    trip_price: 0,
    luggage_fee: 0,
    deposit: 20,
    referral_discount: 0,
    total_due_now: 20
  });

  const [specialTrips, setSpecialTrips] = useState([]);
  const [pricingConfig, setPricingConfig] = useState(null);
  const [minBookingHours, setMinBookingHours] = useState(48);
  const [referralValidation, setReferralValidation] = useState({ status: "idle", message: "" });
  const [slotAvailability, setSlotAvailability] = useState(null);
  const [checkingSlot, setCheckingSlot] = useState(false);

  const checkSlot = async () => {
    if (!formData.travel_date || !formData.travel_time || !formData.trip_type) return;
    setCheckingSlot(true);
    try {
      // For arrival, the airport is the PICKUP; for return, the airport is the DROPOFF.
      const airportHint = (formData.trip_type === "return"
        ? formData.dropoff_location
        : formData.pickup_location) || "";
      const params = new URLSearchParams({
        trip_type: formData.trip_type,
        travel_date: formData.travel_date,
        travel_time: formData.travel_time,
        airport: airportHint || (formData.trip_type === "return" ? "ohare" : "ohare"),
      });
      const res = await axios.get(`${API}/bookings/slot-availability?${params.toString()}`);
      setSlotAvailability(res.data);
    } catch { setSlotAvailability(null); }
    finally { setCheckingSlot(false); }
  };

  useEffect(() => {
    fetchData();
    // Auto-fill referral code from URL (?ref=EASY-XXXXXX) — used by "Share Trip" WhatsApp link.
    try {
      const params = new URLSearchParams(window.location.search);
      const refCode = params.get("ref") || params.get("referral");
      if (refCode) {
        setFormData(prev => ({ ...prev, referral_code: refCode.toUpperCase() }));
      }
      const preDate = params.get("date");
      if (preDate) {
        setFormData(prev => ({ ...prev, travel_date: preDate }));
      }
    } catch (e) { /* URLSearchParams unavailable — skip */ }
  }, []);

  // Debounced referral-code validation
  useEffect(() => {
    const code = (formData.referral_code || "").trim().toUpperCase();
    if (!code) { setReferralValidation({ status: "idle", message: "" }); return; }
    if (code.length < 4) { setReferralValidation({ status: "typing", message: "" }); return; }
    let cancelled = false;
    setReferralValidation({ status: "checking", message: "Checking code…" });
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/referrals/check/${encodeURIComponent(code)}`);
        if (cancelled) return;
        if (res.data.valid) {
          setReferralValidation({ status: "valid", message: res.data.message || "$5 discount applied!" });
        } else {
          setReferralValidation({ status: "invalid", message: res.data.message || "Invalid code" });
        }
      } catch {
        if (!cancelled) setReferralValidation({ status: "invalid", message: "Could not verify" });
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [formData.referral_code]);

  // Debounced check for redeemable referral credits on this passenger's WhatsApp
  useEffect(() => {
    const phone = (formData.whatsapp_number || "").trim();
    if (!phone || phone.length < 8) { setAvailableCredits(0); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/referrals/credits-available/${encodeURIComponent(phone)}`);
        if (!cancelled) setAvailableCredits(Number(res.data?.credits_available) || 0);
      } catch { if (!cancelled) setAvailableCredits(0); }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [formData.whatsapp_number]);

  // Debounced customer-debt lookup — if this passenger owes an unpaid balance,
  // surface it so they see the $50 auto-surcharge before they hit submit.
  const [customerDebt, setCustomerDebt] = useState(null);
  useEffect(() => {
    const phone = ((passengers?.[0]?.whatsapp || formData.whatsapp_number) || "").trim();
    const email = (formData.email || "").trim().toLowerCase();
    const hasPhone = phone && phone.replace(/\D/g, "").length >= 10;
    const hasEmail = email && email.includes("@");
    if (!hasPhone && !hasEmail) { setCustomerDebt(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const phonePart = hasPhone ? encodeURIComponent(phone) : "_";
        const emailQ = hasEmail ? `?email=${encodeURIComponent(email)}` : "";
        const res = await axios.get(`${API}/customer-debt-lookup/${phonePart}${emailQ}`);
        if (!cancelled) setCustomerDebt(res.data?.has_debt ? res.data : null);
      } catch { if (!cancelled) setCustomerDebt(null); }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passengers?.[0]?.whatsapp, formData.whatsapp_number, formData.email]);

  useEffect(() => {
    calculatePricing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.trip_type, formData.pickup_location, formData.num_passengers, formData.total_suitcases, formData.uses_shipping_company, formData.ride_type, formData.pay_full_trip, formData.travel_date, formData.tip_amount, pricingConfig]);

  const fetchData = async () => {
    try {
      const [airportRes, housingRes, specialRes, pricingRes] = await Promise.all([
        axios.get(`${API}/airports`),
        axios.get(`${API}/housing-units`),
        axios.get(`${API}/special-trips`),
        axios.get(`${API}/pricing`)
      ]);
      setAirports(airportRes.data);
      setHousingUnits(housingRes.data);
      setSpecialTrips(specialRes.data?.trips || []);
      setPricingConfig(pricingRes.data?.pricing || null);
      if (typeof pricingRes.data?.min_booking_hours === "number") {
        setMinBookingHours(pricingRes.data.min_booking_hours);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load booking options");
    }
  };

  // Lookup previous booking by confirmation number
  const lookupConfirmation = async () => {
    if (!confirmationNumber || confirmationNumber.length < 4) {
      toast.error("Please enter a valid confirmation number");
      return;
    }
    
    setConfirmationLoading(true);
    try {
      const res = await axios.get(`${API}/bookings/lookup/confirmation/${confirmationNumber}`);
      
      if (res.data.found) {
        setPreviousBooking(res.data);
        
        // Prefill form data with previous booking info
        setFormData(prev => ({
          ...prev,
          full_name: res.data.full_name || prev.full_name,
          whatsapp_number: res.data.whatsapp_number || prev.whatsapp_number,
          email: res.data.email || prev.email,
          housing_unit: res.data.housing_unit || prev.housing_unit,
          housing_address: res.data.housing_address || prev.housing_address,
          housing_phone: res.data.housing_phone || prev.housing_phone,
          num_passengers: res.data.num_passengers || prev.num_passengers
        }));
        
        // Prefill passengers
        if (res.data.passengers && res.data.passengers.length > 0) {
          setPassengers(res.data.passengers.map(p => ({
            name: p.name || p.full_name || "",
            whatsapp: p.whatsapp || p.whatsapp_number || ""
          })));
        } else if (res.data.full_name) {
          const newCount = res.data.num_passengers || 1;
          const newPassengers = [{ name: res.data.full_name, whatsapp: res.data.whatsapp_number }];
          for (let i = 1; i < newCount; i++) {
            newPassengers.push({ name: "", whatsapp: "" });
          }
          setPassengers(newPassengers);
        }
        
        toast.success(`Found your booking! Welcome back, ${res.data.full_name}!`);
      } else {
        toast.error(res.data.message || "Confirmation number not found");
        setPreviousBooking(null);
      }
    } catch (error) {
      console.error("Confirmation lookup error:", error);
      toast.error("Could not find booking with this confirmation number");
      setPreviousBooking(null);
    } finally {
      setConfirmationLoading(false);
    }
  };

  // Start Over function
  const handleStartOver = () => {
    setStep(1);
    setDate(null);
    setCustomerType(null);
    setConfirmationNumber("");
    setPreviousBooking(null);
    setFormData({
      full_name: "",
      whatsapp_number: "",
      email: "",
      airline: "",
      flight_number: "",
      arrival_terminal: "",
      housing_unit: "",
      housing_address: "",
      housing_phone: "",
      trip_type: "arrival",
      pickup_location: "",
      dropoff_location: "",
      travel_date: "",
      travel_time: "",
      num_passengers: 1,
      total_suitcases: null,
    total_carry_ons: 0,
      extra_suitcases: 0,
      uses_shipping_company: false,
      ride_type: "shared",
      payment_method: "stripe",
      referral_code: "",
      departure_time: ""
    });
    setPassengers([{ name: "", whatsapp: "", destination: "", custom_address: "" }]);
    setFlightInfo(null);
    setUseCustomAddress(false);
    toast.info("Form reset. Start your booking fresh!");
  };

  const calculatePricing = async () => {
    const pc = pricingConfig || {};
    const airport = airports.find(a => a.name === formData.pickup_location);
    const zone = airport?.pricing_zone || "ohare";
    
    let tripPrice = 0;
    let luggageFee = 0;
    const depositPerPerson = pc.deposit_per_person || 20;
    let shippingSavings = 0;
    let isSolo = false;
    let otherPaxSameDay = null;
    const perSuitcase = formData.trip_type === "return" && formData.uses_shipping_company
      ? ((pc.return?.per_suitcase_discount) || 5)
      : ((pc.return?.per_suitcase) || 5);

    // 1 FREE suitcase per booking; each additional suitcase = $5 — applies to BOTH trip types.
    // Carry-ons follow the same rule: 1 free, $5 each after.
    const totalSuitcases = Number.isInteger(formData.total_suitcases) ? formData.total_suitcases : 0;
    const extraSuitcases = Math.max(0, totalSuitcases - 1);
    const totalCarryOns = Number.isInteger(formData.total_carry_ons) ? formData.total_carry_ons : 0;
    const extraCarryOns = Math.max(0, totalCarryOns - 1);
    luggageFee = (extraSuitcases + extraCarryOns) * perSuitcase;
    
    if (formData.trip_type === "return") {
      const retConfig = pc.return || {};
      // Madison-airport short-haul return: flat $65/pax regardless of ride type.
      const dropoffLc = (formData.dropoff_location || "").toLowerCase();
      const isMadisonReturn = /madison|msn|dane county|dane regional/.test(dropoffLc);
      if (isMadisonReturn) {
        // $80 solo / $65 shared — same-day grouping auto-drops
        const perPerson = formData.num_passengers >= 2 ? (retConfig.madison_shared || retConfig.madison || 65) : (retConfig.madison_solo || 85);
        tripPrice = perPerson * formData.num_passengers;
      } else if (formData.ride_type === "private") {
        tripPrice = retConfig.solo || retConfig.private || 200;
      } else {
        const perPerson = formData.uses_shipping_company ? (retConfig.shipping_company || 70) : (retConfig.per_person || 85);
        tripPrice = perPerson * formData.num_passengers;

        if (formData.uses_shipping_company) {
          // shipping_company is now an UPCHARGE for luggage forwarding (no longer a discount).
          // Negative shippingSavings is a no-op in the UI (we only render it when positive).
          shippingSavings = ((retConfig.per_person || 85) - (retConfig.shipping_company || 70)) * formData.num_passengers;
        }
      }
    } else {
      const zoneConfig = pc[zone] || pc.ohare || {};
      // Always start with shared rate — backend quote endpoint determines actual solo vs shared
      tripPrice = (zoneConfig.shared || 120) * formData.num_passengers;
    }

    // Query backend quote for accurate solo-vs-shared pricing on ANY trip type
    if (formData.travel_date && formData.pickup_location) {
      try {
        const quoteRes = await axios.post(`${API}/bookings/quote`, {
          trip_type: formData.trip_type,
          pickup_location: formData.pickup_location,
          dropoff_location: formData.dropoff_location || formData.housing_unit || formData.housing_address || "",
          travel_date: formData.travel_date,
          travel_time: formData.travel_time,
          num_passengers: formData.num_passengers,
          ride_type: formData.ride_type,
          total_suitcases: totalSuitcases,
          extra_suitcases: extraSuitcases,
          total_carry_ons: totalCarryOns,
          uses_shipping_company: formData.uses_shipping_company,
          pay_full_trip: formData.pay_full_trip
        });
        if (quoteRes.data?.trip_price !== undefined) {
          tripPrice = quoteRes.data.trip_price;
          isSolo = quoteRes.data.is_solo || false;
          otherPaxSameDay = quoteRes.data.other_passengers_same_day;
        }
      } catch (e) {
        // Fall back to local calc on error
      }
    }
    
    const totalBeforeDiscount = formData.pay_full_trip 
      ? tripPrice + luggageFee 
      : (depositPerPerson * formData.num_passengers) + luggageFee;
    const tipAmount = Math.max(0, parseFloat(formData.tip_amount || 0));
    const totalDueNow = totalBeforeDiscount + (formData.pay_full_trip ? tipAmount : 0);
    
    setPricing({
      trip_price: tripPrice,
      luggage_fee: luggageFee,
      deposit: depositPerPerson * formData.num_passengers,
      deposit_per_person: depositPerPerson,
      num_passengers: formData.num_passengers,
      referral_discount: 0,
      shipping_savings: shippingSavings,
      total_due_now: totalDueNow,
      pay_full_trip: formData.pay_full_trip,
      tip_amount: tipAmount,
      is_solo: isSolo,
      other_passengers_same_day: otherPaxSameDay
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-set dropoff based on housing selection
    if (name === "housing_unit" && value !== "custom") {
      const housing = housingUnits.find(h => h.name === value);
      if (housing) {
        setFormData(prev => ({ 
          ...prev, 
          housing_unit: value,
          housing_address: housing.address,
          dropoff_location: value
        }));
      }
    }
    
    // Update passengers array when num_passengers changes
    if (name === "num_passengers") {
      const newCount = parseInt(value);
      setPassengers(prev => {
        const current = [...prev];
        if (newCount > current.length) {
          for (let i = current.length; i < newCount; i++) {
            current.push({ name: "", whatsapp: "", destination: "", custom_address: "" });
          }
        } else if (newCount < current.length) {
          current.splice(newCount);
        }
        return current;
      });
    }
  };

  const handlePassengerChange = (index, field, value) => {
    setPassengers(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // If it's the first passenger, also update main form data
      if (index === 0 && field === "name") {
        setFormData(prev => ({ ...prev, full_name: value }));
      }
      if (index === 0 && field === "whatsapp") {
        setFormData(prev => ({ ...prev, whatsapp_number: value }));
      }
      
      return updated;
    });
  };

  const handleDateSelect = (selectedDate) => {
    if (selectedDate) {
      setDate(selectedDate);
      const formattedDate = format(selectedDate, "yyyy-MM-dd");
      setFormData(prev => ({ ...prev, travel_date: formattedDate }));
      
      // Auto-lookup flight info if flight number is already entered
      if (formData.flight_number && formData.flight_number.length >= 3) {
        // Delay slightly to ensure state is updated
        setTimeout(() => {
          lookupFlightWithDate(formData.flight_number, formattedDate);
        }, 100);
      }
    }
  };

  // Flight lookup function with explicit date
  const lookupFlightWithDate = async (flightNumber, travelDate) => {
    if (!flightNumber || flightNumber.length < 3) {
      setFlightInfo(null);
      return;
    }
    
    setFlightLoading(true);
    try {
      const dateParam = travelDate ? `?date=${travelDate}` : '';
      const res = await axios.get(`${API}/flight/lookup/${flightNumber}${dateParam}`);
      
      if (res.data.found) {
        setFlightInfo(res.data);
        
        // Auto-fill form data with flight info including arrival time
        setFormData(prev => ({
          ...prev,
          airline: res.data.airline || prev.airline,
          arrival_terminal: res.data.arrival?.terminal || prev.arrival_terminal,
          travel_time: res.data.arrival_time_prefill || prev.travel_time
        }));
        
        // Show route warning if flight doesn't go to/from Chicago area
        if (res.data.route_warning) {
          toast.warning(res.data.route_warning, { duration: 8000 });
        } else if (res.data.source === "live") {
          let message = `Flight found: ${res.data.airline}`;
          if (res.data.arrival_time_prefill) {
            message += ` - Arrives at ${res.data.arrival_time_prefill}`;
          }
          toast.success(message);
        } else {
          toast.info(`Airline: ${res.data.airline} (estimated)`);
        }
      }
    } catch (error) {
      console.error("Flight lookup error:", error);
      setFlightInfo(null);
    } finally {
      setFlightLoading(false);
    }
  };

  // Wrapper function for button click that uses current form state
  const lookupFlight = (flightNumber) => {
    lookupFlightWithDate(flightNumber, formData.travel_date);
  };

  // Handle payment screenshot upload
  const handlePaymentScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      setPaymentScreenshot(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPaymentScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
      toast.success("Payment screenshot uploaded!");
    }
  };

  const removePaymentScreenshot = () => {
    setPaymentScreenshot(null);
    setPaymentScreenshotPreview(null);
  };

  // Compute if travel date/time is within 48 hours
  const getHoursUntilTrip = () => {
    if (!formData.travel_date) return null;
    try {
      const dt = new Date(`${formData.travel_date}T${formData.travel_time || "00:00"}:00`);
      if (isNaN(dt.getTime())) return null;
      return (dt - new Date()) / 36e5;
    } catch (_e) {
      return null;
    }
  };
  const hoursUntilTrip = getHoursUntilTrip();
  const isWithin48Hours = hoursUntilTrip !== null && minBookingHours > 0 && hoursUntilTrip < minBookingHours;

  const validateStep = (currentStep) => {
    switch (currentStep) {
      case 1:
        // travel_time is derived by the system from pickup rules — no longer a Step-1 gate.
        return formData.trip_type && formData.travel_date && !isWithin48Hours;
      case 2:
        return formData.pickup_location && (formData.housing_unit || (useCustomAddress && formData.housing_address));
      case 3: {
        if (!formData.airline?.trim()) {
          toast.error("Please enter your airline name.");
          return false;
        }
        if (!formData.flight_number?.trim()) {
          toast.error("Please enter your flight number so the driver can track your flight.");
          return false;
        }
        if (!formData.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          toast.error("Please enter a valid email address — we send your booking confirmation there.");
          return false;
        }
        // Force explicit suitcase count selection (null/undefined not allowed)
        if (formData.total_suitcases === null || formData.total_suitcases === undefined) {
          toast.error("Please select your total suitcase count.");
          return false;
        }
        // Require every passenger's name + whatsapp
        const paxCount = formData.num_passengers || 1;
        if (!Array.isArray(passengers) || passengers.length < paxCount) return false;
        for (let i = 0; i < paxCount; i++) {
          const p = passengers[i] || {};
          if (!p.name?.trim() || !p.whatsapp?.trim()) {
            toast.error(`Please enter both name and WhatsApp for passenger ${i + 1}.`);
            return false;
          }
        }
        return true;
      }
      case 4:
        return formData.payment_method;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (step === 1 && isWithin48Hours) {
      toast.error(`Online bookings require ${minBookingHours}+ hours notice. Please contact us via WhatsApp or call (815) 914-8139.`);
      setShowValidationErrors(true);
      return;
    }
    if (!validateStep(step)) {
      setShowValidationErrors(true);
      // Step 3 emits its own specific toasts (email/flight/passenger) — avoid double-toasting there
      if (step !== 3) toast.error("Please fill in all required fields");
      return;
    }
    setShowValidationErrors(false);
    setStep(prev => Math.min(prev + 1, 5));
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (isWithin48Hours) {
      toast.error(`Online bookings require ${minBookingHours}+ hours notice. Please WhatsApp or call (815) 914-8139.`);
      setStep(1);
      return;
    }
    setLoading(true);
    try {
      // Set dropoff location for return trips
      let finalFormData = { ...formData };
      if (formData.trip_type === "return") {
        finalFormData.dropoff_location = formData.pickup_location;
        finalFormData.pickup_location = formData.housing_unit || formData.housing_address;
        // Backend uses `departure_time` to auto-compute the driver pickup time. Passenger
        // never enters pickup time directly — send them the same flight time.
        finalFormData.departure_time = formData.travel_time;
      } else {
        finalFormData.dropoff_location = formData.housing_unit || formData.housing_address;
      }
      
      if (useCustomAddress) {
        finalFormData.housing_unit = "Custom Address";
      }
      
      // Include passengers array with all passenger details
      finalFormData.passengers = passengers;

      // Coerce null suitcase count to 0 as a defensive fallback (validation should already gate this)
      finalFormData.total_suitcases = Number.isInteger(formData.total_suitcases) ? formData.total_suitcases : 0;
      
      // Set full_name and whatsapp_number from first passenger if not set
      if (passengers.length > 0 && passengers[0].name) {
        finalFormData.full_name = passengers[0].name;
        finalFormData.whatsapp_number = passengers[0].whatsapp;
      }
      
      const response = await axios.post(`${API}/bookings`, finalFormData);
      const bookingId = response.data.booking_id;
      
      // Handle payment based on method
      if (formData.payment_method === "stripe" || formData.payment_method === "apple_pay") {
        // Apple Pay uses Stripe checkout (Stripe handles Apple Pay automatically)
        const paymentRes = await axios.post(
          `${API}/payments/stripe/checkout?booking_id=${bookingId}&origin_url=${window.location.origin}`
        );
        window.location.href = paymentRes.data.checkout_url;
      } else if (formData.payment_method === "paypal") {
        const paymentRes = await axios.post(`${API}/payments/paypal/create?booking_id=${bookingId}`);
        localStorage.setItem("pendingBooking", JSON.stringify({ bookingId, ...paymentRes.data }));
        navigate(`/booking/success?booking_id=${bookingId}&payment=paypal`);
      } else {
        // CashApp or Zelle - manual payment
        const paymentRes = await axios.post(
          `${API}/payments/manual/initiate?booking_id=${bookingId}&payment_method=${formData.payment_method}`
        );
        localStorage.setItem("paymentInfo", JSON.stringify(paymentRes.data));
        navigate(`/booking/success?booking_id=${bookingId}&payment=manual`);
      }
    } catch (error) {
      console.error("Booking failed:", error);
      if (error.response?.status === 409) {
        toast.error(error.response.data.detail || "This booking already exists. Please contact us to modify it.");
      } else {
        toast.error("Failed to create booking. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, title: "Trip Details", icon: CalendarIcon },
    { num: 2, title: "Route", icon: MapPin },
    { num: 3, title: "Your Info", icon: User },
    { num: 4, title: "Payment", icon: CreditCard },
    { num: 5, title: "Confirm", icon: Check }
  ];

  return (
    <div className="min-h-screen bg-background py-4 px-2 sm:py-8 sm:px-4" data-testid="booking-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-4 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-foreground mb-1 sm:mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Book Your Trip
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">Easy Transportation - Easy Ride, Exceptional Experience</p>
        </div>

        {/* Progress Steps */}
        <BookingStepProgress step={step} steps={steps} />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
          {/* Main Form */}
          <div className="md:col-span-2">
            <Card className="shadow-xl border-0 rounded-3xl overflow-hidden">
              <CardContent className="p-4 sm:p-6 md:p-8">
                {/* Step 1: Trip Details */}
                {step === 1 && (
                  <div className="space-y-6" data-testid="step-1">
                    <div>
                      <Label className="text-base font-semibold mb-4 block">Trip Type</Label>
                      <RadioGroup
                        value={formData.trip_type}
                        onValueChange={(value) => {
                          handleSelectChange("trip_type", value);
                          // Reset customer type when changing trip type
                          setCustomerType(null);
                          setPreviousBooking(null);
                          setConfirmationNumber("");
                        }}
                        className="grid grid-cols-2 gap-3 sm:gap-4"
                      >
                        <Label 
                          htmlFor="arrival"
                          className={`flex flex-col items-center p-3 sm:p-6 rounded-2xl border-2 cursor-pointer transition-colors ${
                            formData.trip_type === "arrival" ? "border-green-600 bg-green-50" : "border-muted hover:border-green-300"
                          }`}
                        >
                          <RadioGroupItem value="arrival" id="arrival" className="sr-only" />
                          <Plane className="w-6 h-6 sm:w-8 sm:h-8 mb-1 sm:mb-2 text-green-600" />
                          <span className="font-bold text-sm sm:text-base">Arrival</span>
                          <span className="text-xs text-muted-foreground text-center">Airport to Housing</span>
                        </Label>
                        <Label 
                          htmlFor="return"
                          className={`flex flex-col items-center p-3 sm:p-6 rounded-2xl border-2 cursor-pointer transition-colors ${
                            formData.trip_type === "return" ? "border-green-600 bg-green-50" : "border-muted hover:border-green-300"
                          }`}
                        >
                          <RadioGroupItem value="return" id="return" className="sr-only" />
                          <Home className="w-6 h-6 sm:w-8 sm:h-8 mb-1 sm:mb-2 text-green-600" />
                          <span className="font-bold text-sm sm:text-base">Return</span>
                          <span className="text-xs text-muted-foreground text-center">Housing to Airport</span>
                          <span className="text-xs text-yellow-600 font-semibold mt-1">${(pricingConfig?.return?.per_person) || 65}/person</span>
                          <span className="text-[10px] text-orange-600 mt-0.5 text-center px-1">+${Math.max(0, ((pricingConfig?.return?.shipping_company) || 85) - ((pricingConfig?.return?.per_person) || 65))} if shipping with T&amp;M</span>
                        </Label>
                      </RadioGroup>
                    </div>

                    {/* Ride type is auto-determined by the system based on other bookings for the same date/airport */}
                    {formData.trip_type === "arrival" && (
                      <div className="p-3 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border border-emerald-200">
                        <div className="flex items-center gap-2">
                          <Users className="w-5 h-5 text-emerald-600" />
                          <div>
                            <p className="text-sm font-semibold text-emerald-800">Ride type is set automatically</p>
                            <p className="text-xs text-emerald-700">If other passengers are booked for the same date and airport, you get the shared rate. Otherwise the solo rate applies. Check below to see who else is booked.</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Return Trip: First time or Returning question */}
                    {formData.trip_type === "return" && (
                      <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                        <Label className="text-sm font-bold text-blue-800 mb-3 block">
                          <Ticket className="inline w-4 h-4 mr-1" />
                          Are you booking with us for the first time or returning home after arriving with us this Summer {new Date().getFullYear()}?
                        </Label>
                        
                        <RadioGroup
                          value={customerType || ""}
                          onValueChange={(value) => {
                            setCustomerType(value);
                            if (value === "first_time") {
                              setPreviousBooking(null);
                              setConfirmationNumber("");
                            }
                          }}
                          className="grid grid-cols-1 md:grid-cols-2 gap-3"
                        >
                          <Label 
                            htmlFor="first_time"
                            className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                              customerType === "first_time" ? "border-blue-600 bg-blue-50" : "border-gray-200 hover:border-blue-300"
                            }`}
                          >
                            <RadioGroupItem value="first_time" id="first_time" className="sr-only" />
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${customerType === "first_time" ? "bg-blue-600 text-white" : "bg-gray-200"}`}>
                                <User className="w-4 h-4" />
                              </div>
                              <div>
                                <span className="font-bold block">First Time</span>
                                <span className="text-xs text-muted-foreground">New booking</span>
                              </div>
                            </div>
                          </Label>
                          <Label 
                            htmlFor="returning"
                            className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                              customerType === "returning" ? "border-green-600 bg-green-50" : "border-gray-200 hover:border-green-300"
                            }`}
                          >
                            <RadioGroupItem value="returning" id="returning" className="sr-only" />
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${customerType === "returning" ? "bg-green-600 text-white" : "bg-gray-200"}`}>
                                <RotateCcw className="w-4 h-4" />
                              </div>
                              <div>
                                <span className="font-bold block">Returning Home - Summer {new Date().getFullYear()}</span>
                                <span className="text-xs text-muted-foreground">I arrived with Easy Transportation</span>
                              </div>
                            </div>
                          </Label>
                        </RadioGroup>

                        {/* Confirmation Number Input for Returning Customers */}
                        {customerType === "returning" && (
                          <div className="mt-4 p-4 bg-white rounded-lg border border-green-200">
                            <Label className="text-sm font-medium text-gray-700 mb-2 block">
                              Enter your arrival confirmation number for easy return trip booking:
                            </Label>
                            <div className="flex gap-2">
                              <Input
                                value={confirmationNumber}
                                onChange={(e) => setConfirmationNumber(e.target.value.toUpperCase())}
                                placeholder="e.g., ABC12345"
                                className="h-11 uppercase"
                                data-testid="confirmation-number-input"
                              />
                              <Button 
                                type="button"
                                onClick={lookupConfirmation}
                                disabled={confirmationLoading || !confirmationNumber}
                                className="h-11 px-4 bg-green-600 hover:bg-green-700"
                              >
                                {confirmationLoading ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Search className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                            
                            {previousBooking && (
                              <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-300">
                                <p className="text-sm text-green-800">
                                  <Check className="inline w-4 h-4 mr-1" />
                                  <strong>Welcome back, {previousBooking.full_name}!</strong>
                                </p>
                                <p className="text-xs text-green-700 mt-1">
                                  Your information has been pre-filled. You arrived on {previousBooking.original_arrival_date}.
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* DATE AND FLIGHT NUMBER - FIRST INPUTS */}
                    <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-bold text-green-800 flex items-center gap-2">
                          <CalendarIcon className="w-4 h-4" />
                          Travel Date & Flight Information
                        </h4>
                        <span className="text-xs text-red-500">* Required fields</span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <RequiredLabel>
                            Flight Date
                            <ErrorArrow show={showValidationErrors && !formData.travel_date} />
                          </RequiredLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                type="button"
                                data-testid="date-picker-trigger"
                                className={`w-full h-12 justify-start text-left font-normal rounded-xl bg-white border-[3px] ${showValidationErrors && !formData.travel_date ? 'border-red-500 animate-pulse' : 'border-red-500'} hover:bg-gray-50 hover:border-red-600`}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {date ? format(date, "PPP") : "Select date"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 z-[100]" align="start">
                              <Calendar
                                mode="single"
                                selected={date}
                                onSelect={(selectedDate) => {
                                  if (selectedDate) {
                                    handleDateSelect(selectedDate);
                                  }
                                }}
                                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div>
                          <RequiredLabel>
                            {formData.trip_type === "arrival" ? "Flight Arrival Time" : "Flight Departure Time"}
                            <ErrorArrow show={showValidationErrors && !formData.travel_time} />
                          </RequiredLabel>
                          <Input
                            type="time"
                            name="travel_time"
                            value={formData.travel_time}
                            onChange={handleInputChange}
                            data-testid="travel-time-input"
                            className={`h-12 rounded-xl bg-white border-[3px] border-red-500 focus:border-red-600 ${showValidationErrors && !formData.travel_time ? 'animate-pulse' : ''}`}
                          />
                          {formData.trip_type === "return" && (
                            <p className="text-[11px] text-emerald-700 mt-1 font-medium">
                              Pickup time is set automatically based on your flight time.
                            </p>
                          )}
                        </div>
                        <div>
                          <RequiredLabel>
                            <span className="flex items-center gap-1">
                              <Plane className="w-3.5 h-3.5" />
                              Flight Number
                              <ErrorArrow show={showValidationErrors && !formData.flight_number} />
                            </span>
                          </RequiredLabel>
                          <div className="flex gap-2">
                            <Input
                              name="flight_number"
                              value={formData.flight_number}
                              onChange={handleInputChange}
                              onBlur={(e) => {
                                const fn = (e.target.value || "").trim();
                                if (fn.length >= 3 && fn !== (flightInfo?.flight_number || "").trim()) {
                                  lookupFlight(fn);
                                }
                              }}
                              placeholder="e.g., UA123"
                              data-testid="flight-number-input"
                              className={`h-12 rounded-xl bg-white flex-1 uppercase border-[3px] border-red-500 ${showValidationErrors && !formData.flight_number ? 'animate-pulse' : ''}`}
                            />
                            <Button 
                              type="button"
                              onClick={() => lookupFlight(formData.flight_number)}
                              disabled={flightLoading || !formData.flight_number}
                              className="h-12 px-4 bg-blue-600 hover:bg-blue-700"
                              title="Look up flight info"
                            >
                              {flightLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Search className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Click search to auto-fill airline</p>
                        </div>
                      </div>
                      
                      <p className="text-[13px] font-extrabold text-fuchsia-600 uppercase animate-pulse mb-4" data-testid="flight-date-helper">
                        Flight number and Flight date required — not travel time!
                      </p>

                      {/* Auto-calculated pickup time preview for return trips */}
                      {formData.trip_type === "return" && formData.travel_time && formData.travel_date && (() => {
                        const [hh, mm] = formData.travel_time.split(":").map(Number);
                        const mins = hh * 60 + mm;
                        const [y, m, d] = formData.travel_date.split("-").map(Number);
                        const flightDate = new Date(Date.UTC(y, m - 1, d));
                        let pickupDate, pickupTime;
                        if (mins > 17 * 60) {
                          pickupDate = flightDate;
                          pickupTime = "11:00 AM";
                        } else {
                          pickupDate = new Date(flightDate.getTime() - 86400000);
                          pickupTime = "11:00 PM";
                        }
                        const fmt = pickupDate.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", timeZone: "UTC" });
                        return (
                          <div className="bg-blue-50 border-2 border-blue-300 rounded-2xl p-3 mb-4 flex items-start gap-3" data-testid="auto-pickup-time-preview">
                            <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm font-bold text-blue-900">Driver Pickup Time (auto-set)</p>
                              <p className="text-sm text-blue-800">
                                <span className="font-bold">{pickupTime}</span> on <span className="font-bold">{fmt}</span>
                              </p>
                              <p className="text-[11px] text-blue-700 mt-1">
                                Flights 3 AM–5 PM → pickup 11 PM the day before · Flights after 5 PM → pickup 11 AM the same day.
                              </p>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Configurable Booking Window Warning */}
                      {isWithin48Hours && formData.travel_date && formData.travel_time && (
                        <div className="mb-4 bg-red-50 border-2 border-red-300 rounded-2xl p-4" data-testid="within-48h-warning">
                          <div className="flex items-start gap-3 mb-3">
                            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <h4 className="font-bold text-red-900 mb-1">Less than {minBookingHours} hours notice</h4>
                              <p className="text-sm text-red-800">
                                Online bookings require at least <span className="font-bold">{minBookingHours} hours advance notice</span>.
                                For trips within {Math.max(1, Math.round(hoursUntilTrip))} hours, please contact us directly to check driver availability.
                              </p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                            <a
                              href="https://wa.me/18159148139?text=Hi%2C%20I%20need%20to%20book%20a%20trip%20within%2048%20hours."
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid="urgent-whatsapp-btn"
                              className="w-full"
                            >
                              <Button type="button" className="w-full bg-green-600 hover:bg-green-700 text-white rounded-full h-11 font-semibold">
                                <MessageCircle className="w-4 h-4 mr-2" />
                                WhatsApp (815) 914-8139
                              </Button>
                            </a>
                            <a href="tel:+18159148139" data-testid="urgent-call-btn" className="w-full">
                              <Button type="button" variant="outline" className="w-full border-2 border-red-600 text-red-700 hover:bg-red-50 rounded-full h-11 font-semibold">
                                <Phone className="w-4 h-4 mr-2" />
                                Call (815) 914-8139
                              </Button>
                            </a>
                          </div>
                          <p className="text-xs text-red-700 mt-2 text-center">
                            Our team responds to last-minute requests as quickly as possible.
                          </p>
                        </div>
                      )}

                      {/* Departure Time is now DERIVED from `travel_time` on submit for return trips. */}

                      {/* See Who Else Is Going — anonymized slot check so passengers know if they auto-qualify for shared rate */}
                      {formData.travel_date && formData.travel_time && (
                        <div className="mb-4">
                          <button
                            type="button"
                            onClick={checkSlot}
                            disabled={checkingSlot}
                            className="w-full text-left bg-indigo-50 border-2 border-indigo-200 rounded-xl px-4 py-3 hover:bg-indigo-100 transition-colors disabled:opacity-60"
                            data-testid="check-slot-btn"
                          >
                            <div className="flex items-center gap-2">
                              <Users className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="text-sm font-bold text-indigo-900">
                                  {checkingSlot ? "Checking…" : slotAvailability ? "Check again" : "See who else is booked for this date"}
                                </p>
                                <p className="text-xs text-indigo-700">Find out if you&apos;ll auto-qualify for the shared-ride rate.</p>
                              </div>
                              <ArrowRight className="w-4 h-4 text-indigo-600" />
                            </div>
                          </button>

                          {slotAvailability && (
                            <div className={`mt-2 rounded-xl p-3 border-2 ${slotAvailability.will_be_shared ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"}`} data-testid="slot-availability-result">
                              {slotAvailability.will_be_shared ? (
                                <div className="flex items-start gap-2">
                                  <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <p className="text-sm font-bold text-emerald-800" data-testid="slot-shared-message">
                                      🎉 {slotAvailability.existing_passengers} other passenger{slotAvailability.existing_passengers === 1 ? " is" : "s are"} already booked for {formData.travel_date} — {slotAvailability.time_window} pickup to {slotAvailability.airport.toUpperCase()}
                                    </p>
                                    <p className="text-xs text-emerald-700 mt-1">
                                      Book now and you&apos;ll auto-qualify for the shared rate: <span className="line-through">${slotAvailability.solo_price}</span> → <span className="font-bold">${slotAvailability.shared_price}/person</span>.
                                    </p>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-start gap-2">
                                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <p className="text-sm font-bold text-amber-900" data-testid="slot-solo-message">
                                      No other passengers booked yet for this slot
                                    </p>
                                    <p className="text-xs text-amber-800 mt-1">
                                      You&apos;ll start at the solo rate <span className="font-bold">${slotAvailability.solo_price}</span> — if a friend books the same day + airport + time-window, both drop to <span className="font-bold">${slotAvailability.shared_price}/person</span>. <a href="#" onClick={(e) => { e.preventDefault(); toast.info("Complete this booking, then use the Share Trip button on the confirmation page."); }} className="underline">Share this trip</a> to save more.
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Referral Code Input — auto-fills from ?ref= URL param when arriving via Share Trip link */}
                      <div className="mb-4">
                        <Label className="text-sm font-medium text-gray-600 mb-2 block flex items-center gap-1">
                          <Users className="w-4 h-4 text-emerald-600" />
                          Referred by a friend?
                          <span className="text-gray-400 ml-1 text-xs">(optional)</span>
                        </Label>
                        <div className="relative">
                          <Input
                            name="referral_code"
                            value={formData.referral_code || ""}
                            onChange={(e) => setFormData({ ...formData, referral_code: e.target.value.toUpperCase() })}
                            data-testid="referral-code-input"
                            className={`h-11 rounded-xl bg-white font-mono uppercase ${
                              referralValidation.status === "valid" ? "border-emerald-500 border-2" :
                              referralValidation.status === "invalid" ? "border-red-400 border-2" :
                              "border-gray-300"
                            }`}
                            placeholder="EASY-XXXXXX"
                            maxLength={12}
                            autoComplete="off"
                          />
                          {referralValidation.status === "checking" && (
                            <span className="absolute right-3 top-3 text-xs text-gray-400" data-testid="referral-checking">Checking…</span>
                          )}
                          {referralValidation.status === "valid" && (
                            <span className="absolute right-3 top-3 text-emerald-600" data-testid="referral-valid">
                              <Check className="w-5 h-5" />
                            </span>
                          )}
                        </div>
                        {referralValidation.message && (
                          <p
                            className={`text-xs mt-1 ${
                              referralValidation.status === "valid" ? "text-emerald-700 font-medium" :
                              referralValidation.status === "invalid" ? "text-red-600" : "text-gray-500"
                            }`}
                            data-testid="referral-message"
                          >
                            {referralValidation.status === "valid" && "✓ "}{referralValidation.message}
                          </p>
                        )}
                        {referralValidation.status === "idle" && (
                          <p className="text-[11px] text-gray-500 mt-1">
                            Have a code from a friend? Enter it here — you both get $5 credit.
                          </p>
                        )}
                      </div>

                      {/* Flight Info Display */}
                      {flightInfo && (() => {
                        const airlineCode = getAirlineCode(flightInfo.flight_number);
                        const colors = getAirlineColors(airlineCode);
                        const status = getFlightStatus(flightInfo.status, flightInfo.delayed);
                        
                        return (
                          <div className={`p-3 rounded-lg border-2 ${colors.border} ${colors.light}`}>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              {/* Airline with color badge */}
                              <div>
                                <span className="text-muted-foreground text-xs">Airline</span>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-white font-bold text-xs ${colors.bg}`}>
                                    {airlineCode}
                                  </span>
                                  <p className={`font-bold ${colors.text}`}>{flightInfo.airline}</p>
                                </div>
                              </div>
                              
                              {/* Flight Number */}
                              <div>
                                <span className="text-muted-foreground text-xs">Flight</span>
                                <p className="font-bold text-lg mt-1">{flightInfo.flight_number}</p>
                              </div>
                              
                              {/* Terminal */}
                              {flightInfo.arrival?.terminal && (
                                <div>
                                  <span className="text-muted-foreground text-xs">Arrival Terminal</span>
                                  <p className="font-bold text-lg mt-1 text-green-600">Terminal {flightInfo.arrival.terminal}</p>
                                </div>
                              )}
                              
                              {/* Status with delay indicator */}
                              <div>
                                <span className="text-muted-foreground text-xs">Status</span>
                                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold mt-1 ${status.bg} ${status.color}`}>
                                  {status.isDelayed ? (
                                    <AlertCircle className="w-4 h-4" />
                                  ) : status.label === "On Time" || status.label === "Landed" ? (
                                    <Check className="w-4 h-4" />
                                  ) : (
                                    <Clock className="w-4 h-4" />
                                  )}
                                  {status.label}
                                </div>
                              </div>
                            </div>
                            
                            {/* Additional flight details */}
                            {flightInfo.source === "live" && (
                              <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                {flightInfo.departure?.airport && (
                                  <div>
                                    <span className="text-muted-foreground">From</span>
                                    <p className="font-medium">{flightInfo.departure.iata} - {flightInfo.departure.airport}</p>
                                  </div>
                                )}
                                {flightInfo.arrival?.airport && (
                                  <div>
                                    <span className="text-muted-foreground">To</span>
                                    <p className="font-medium">{flightInfo.arrival.iata} - {flightInfo.arrival.airport}</p>
                                  </div>
                                )}
                                {flightInfo.arrival?.gate && (
                                  <div>
                                    <span className="text-muted-foreground">Gate</span>
                                    <p className="font-medium">Gate {flightInfo.arrival.gate}</p>
                                  </div>
                                )}
                                {flightInfo.arrival?.baggage && (
                                  <div>
                                    <span className="text-muted-foreground">Baggage</span>
                                    <p className="font-medium">Carousel {flightInfo.arrival.baggage}</p>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {flightInfo.source === "estimated" && (
                              <p className="text-xs text-yellow-600 mt-2 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                Estimated based on airline data
                              </p>
                            )}
                            {flightInfo.route_warning && (
                              <div className="mt-2 p-2 bg-red-50 rounded-lg border border-red-200">
                                <p className="text-xs text-red-700 flex items-center gap-1 font-medium">
                                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                                  {flightInfo.route_warning}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Label className="text-sm font-medium text-gray-600 mb-2 block">
                        <Users className="inline w-4 h-4 mr-1" />
                        Number of Passengers
                      </Label>
                      <Select
                        value={String(formData.num_passengers)}
                        onValueChange={(value) => handleSelectChange("num_passengers", parseInt(value))}
                      >
                        <SelectTrigger className="h-12 rounded-xl bg-gray-50 border-transparent" data-testid="passengers-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 passenger</SelectItem>
                          <SelectItem value="2">2 passengers</SelectItem>
                          <SelectItem value="3">3 passengers</SelectItem>
                          <SelectItem value="4">4 passengers</SelectItem>
                          <SelectItem value="5">5+ passengers</SelectItem>
                        </SelectContent>
                      </Select>
                      {formData.num_passengers >= 2 && (
                        <p className="mt-1 text-[11px] font-semibold text-emerald-700" data-testid="passenger-count-hint">
                          💰 Group of {formData.num_passengers} — you unlocked the shared-ride rate. See discount in the summary.
                        </p>
                      )}
                      {formData.num_passengers === 1 && (
                        <p className="mt-1 text-[11px] text-gray-600" data-testid="passenger-count-hint">
                          Add a 2nd passenger to unlock the discounted shared-ride rate.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 2: Route */}
                {step === 2 && (
                  <div className="space-y-6" data-testid="step-2">
                    <div>
                      <RequiredLabel>
                        {formData.trip_type === "arrival" ? "Pickup Airport" : "Dropoff Airport"}
                        <ErrorArrow show={showValidationErrors && !formData.pickup_location} />
                      </RequiredLabel>
                      <Select
                        value={formData.pickup_location}
                        onValueChange={(value) => handleSelectChange("pickup_location", value)}
                      >
                        <SelectTrigger className={`h-12 rounded-xl bg-gray-50 border-[3px] border-red-500 ${showValidationErrors && !formData.pickup_location ? 'animate-pulse' : ''}`} data-testid="airport-select">
                          <SelectValue placeholder="Select airport" />
                        </SelectTrigger>
                        <SelectContent>
                          {airports.map((airport) => (
                            <SelectItem key={airport.id} value={airport.name}>
                              {airport.name} ({airport.code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <RequiredLabel>
                        {formData.trip_type === "arrival" ? "Destination Housing" : "Pickup Location"}
                        <ErrorArrow show={showValidationErrors && !formData.housing_unit && !useCustomAddress} />
                      </RequiredLabel>
                      
                      {!useCustomAddress ? (
                        <>
                          <Select
                            value={formData.housing_unit}
                            onValueChange={(value) => handleSelectChange("housing_unit", value)}
                          >
                            <SelectTrigger className={`h-12 rounded-xl bg-gray-50 border-[3px] border-red-500 ${showValidationErrors && !formData.housing_unit ? 'animate-pulse' : ''}`} data-testid="housing-select">
                              <SelectValue placeholder="Select your housing unit" />
                            </SelectTrigger>
                            <SelectContent>
                              {housingUnits.map((housing) => (
                                <SelectItem key={housing.id} value={housing.name}>
                                  {housing.name} - {housing.address}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="mt-4 p-4 bg-amber-50 rounded-xl border-2 border-amber-300 border-dashed">
                            <div className="flex items-center space-x-2">
                              <Checkbox 
                                id="custom-address" 
                                checked={useCustomAddress}
                                onCheckedChange={setUseCustomAddress}
                              />
                              <label htmlFor="custom-address" className="text-sm font-semibold text-amber-800 cursor-pointer">
                                My location is not in the list - Enter custom address
                              </label>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="p-4 bg-amber-50 rounded-xl border-2 border-amber-300">
                            <label className="text-sm font-semibold text-amber-800 mb-2 block">Enter Your Address</label>
                            <AddressAutocomplete
                              value={formData.housing_address}
                              onChange={(val) => setFormData(prev => ({ ...prev, housing_address: val }))}
                              placeholder="Start typing your full address..."
                              dataTestId="custom-address-input"
                              className="h-14 rounded-xl bg-white border-amber-300 focus:border-green-500 text-base"
                            />
                            <div className="flex items-center space-x-2 mt-3">
                              <Checkbox 
                                id="use-list" 
                                checked={!useCustomAddress}
                                onCheckedChange={() => setUseCustomAddress(false)}
                              />
                              <label htmlFor="use-list" className="text-sm text-amber-700 cursor-pointer font-medium">
                                Go back to housing list
                              </label>
                            </div>
                          </div>
                        </>
                      )}
                      
                      {/* Special Request Ride */}
                      <div className="p-3 bg-purple-50 rounded-xl border border-purple-200 mt-3">
                        <p className="text-xs text-purple-700">
                          <strong>Destination not listed?</strong> Contact us for a special request ride:{" "}
                          <a href="tel:8159148139" className="text-purple-600 font-bold underline">(815) 914-8139</a> or{" "}
                          <a href="https://wa.me/18159148139?text=Hi!%20I%20need%20a%20ride%20to%20a%20destination%20not%20listed." target="_blank" rel="noopener noreferrer" className="text-green-600 font-bold underline">WhatsApp</a>
                        </p>
                      </div>
                    </div>

                    {formData.trip_type === "return" && (
                      <>
                        <div className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border border-orange-200">
                          <div className="flex items-start space-x-3">
                            <Checkbox 
                              id="shipping-company" 
                              checked={formData.uses_shipping_company}
                              onCheckedChange={(checked) => handleSelectChange("uses_shipping_company", checked)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <label htmlFor="shipping-company" className="text-sm font-bold cursor-pointer text-orange-800">
                                <Package className="inline w-4 h-4 mr-1" />
                                Ship your luggage with T&amp;M Courier Services
                              </label>
                              <p className="text-xs text-orange-700 mt-1">
                                <span className="font-bold text-green-700">Save ${Math.max(0, ((pricingConfig?.return?.per_person) || 85) - ((pricingConfig?.return?.shipping_company) || 70))}/person</span> — fare drops from <span className="line-through">${(pricingConfig?.return?.per_person) || 85}</span> to <span className="font-bold text-green-700">${(pricingConfig?.return?.shipping_company) || 70}/person</span> when you ship with us and your bags ship separately.
                              </p>
                              <div className="mt-2 text-xs text-gray-600">
                                <p className="font-medium mb-1">Ship your items separately:</p>
                                <div className="flex flex-wrap gap-1">
                                  {["Barrels", "Extra Suitcases", "Scooters", "Bicycles", "TVs", "Heavy Items"].map(item => (
                                    <span key={item} className="px-2 py-0.5 bg-white rounded-full text-orange-700 border border-orange-200">{item}</span>
                                  ))}
                                </div>
                              </div>
                              <p className="text-xs text-orange-600 mt-2">
                                📞 T&M Courier: (815) 914-8139 | Tmcourierservice1@gmail.com
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                          <p className="text-sm text-blue-800 font-medium">
                            <Clock className="inline w-4 h-4 mr-1" />
                            Early Morning Flight? (Before 6 AM)
                          </p>
                          <p className="text-xs text-blue-600 mt-1">
                            We offer late-night pickup runs (11 PM / midnight) for early morning flights. 
                            Our team will contact you to arrange the best pickup time.
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Step 3: Personal Info */}
                {step === 3 && (
                  <div className="space-y-4" data-testid="step-3">
                    {/* All Passengers Info */}
                    <div className="p-4 bg-blue-50 rounded-xl border-2 border-blue-300 mb-4">
                      <h4 className="font-bold text-blue-800 mb-1 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Passenger Information ({formData.num_passengers} {formData.num_passengers === 1 ? 'person' : 'people'})
                      </h4>
                      <p className="text-xs text-blue-700 font-medium">
                        Required: full name <span className="font-bold">and</span> WhatsApp number for <span className="font-bold">every</span> traveler.
                        Each passenger will receive their own trip confirmation.
                      </p>
                      {/* Live running total — updates as passengers are added / removed */}
                      <div className="mt-3 flex items-center justify-between bg-white border-2 border-emerald-400 rounded-xl px-3 py-2 shadow-sm" data-testid="passenger-live-total">
                        <span className="text-xs font-bold text-emerald-800 uppercase">
                          {formData.num_passengers} passenger{formData.num_passengers === 1 ? '' : 's'} · running total
                        </span>
                        <span className="text-lg font-extrabold text-emerald-700" data-testid="passenger-live-total-amount">
                          ${(
                            (pricing.trip_price || 0)
                            + (pricing.luggage_fee || 0)
                            + (formData.tip_amount || 0)
                            - (pricing.shipping_savings || 0)
                            + (customerDebt && customerDebt.auto_apply_active ? (customerDebt.surcharge_per_booking || 50) : 0)
                          ).toFixed(0)}
                        </span>
                      </div>
                    </div>

                    {passengers.map((passenger, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                        <Label className="text-sm font-bold text-gray-700 mb-3 block">
                          Passenger {index + 1} {index === 0 && "(Main Contact)"}
                        </Label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <RequiredLabel className="text-xs">
                              Full Name
                              <ErrorArrow show={showValidationErrors && !passenger.name} />
                            </RequiredLabel>
                            <Input
                              value={passenger.name}
                              onChange={(e) => handlePassengerChange(index, "name", e.target.value)}
                              placeholder={`Passenger ${index + 1} full name`}
                              data-testid={`passenger-${index}-name`}
                              className={`h-10 rounded-lg bg-white border-[3px] border-red-500 ${showValidationErrors && !passenger.name ? 'animate-pulse' : ''}`}
                              required
                            />
                          </div>
                          <div>
                            <RequiredLabel className="text-xs">
                              WhatsApp Number
                              <ErrorArrow show={showValidationErrors && !passenger.whatsapp} />
                            </RequiredLabel>
                            <PhoneInput
                              value={passenger.whatsapp}
                              onChange={(val) => handlePassengerChange(index, "whatsapp", val)}
                              placeholder="Phone number"
                              dataTestId={`passenger-${index}-whatsapp`}
                              className={`h-10 rounded-lg bg-white border-[3px] border-red-500 ${showValidationErrors && !passenger.whatsapp ? 'animate-pulse' : ''}`}
                            />
                          </div>
                        </div>
                        {/* Per-passenger destination (only for multi-passenger + different destinations) */}
                        {formData.num_passengers > 1 && !sameDestination && index > 0 && (
                          <div className="mt-3 space-y-2">
                            <Label className="text-xs font-semibold text-gray-600">Drop-off Destination</Label>
                            <Select
                              value={passenger.destination || ""}
                              onValueChange={(val) => handlePassengerChange(index, "destination", val)}
                            >
                              <SelectTrigger className="h-10 bg-white" data-testid={`passenger-${index}-destination`}>
                                <SelectValue placeholder="Select housing unit" />
                              </SelectTrigger>
                              <SelectContent>
                                {housingUnits.map(h => (
                                  <SelectItem key={h.id || h.name} value={h.name}>{h.name}</SelectItem>
                                ))}
                                <SelectItem value="custom">Other (type address)</SelectItem>
                              </SelectContent>
                            </Select>
                            {passenger.destination === "custom" && (
                              <AddressAutocomplete
                                value={passenger.custom_address || ""}
                                onChange={(val) => handlePassengerChange(index, "custom_address", val)}
                                placeholder="Start typing address..."
                                dataTestId={`passenger-${index}-custom-address`}
                                className="h-10 bg-white"
                              />
                            )}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Same destination checkbox for multi-passenger */}
                    {formData.num_passengers > 1 && (
                      <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl border border-blue-200">
                        <input
                          type="checkbox"
                          id="same-destination"
                          checked={sameDestination}
                          onChange={(e) => setSameDestination(e.target.checked)}
                          className="w-4 h-4 text-green-600 rounded"
                          data-testid="same-destination-checkbox"
                        />
                        <label htmlFor="same-destination" className="text-sm text-blue-800 cursor-pointer">
                          All passengers going to the same destination
                        </label>
                      </div>
                    )}

                    {/* Email for main contact */}
                    <div>
                      <OptionalLabel>Email (for confirmation)</OptionalLabel>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your@email.com"
                        data-testid="email-input"
                        className="h-12 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-green-500"
                      />
                      <p className="text-xs text-muted-foreground mt-1">We'll send booking confirmation via WhatsApp and Email</p>
                    </div>

                    {/* WhatsApp Opt-in Encouragement */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-2xl p-4" data-testid="whatsapp-optin-banner">
                      <div className="flex items-start gap-3">
                        <div className="bg-green-600 rounded-full p-2 flex-shrink-0">
                          <MessageCircle className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-green-900 text-sm mb-1">⚡ Activate WhatsApp Confirmations Now</h4>
                          <p className="text-xs text-green-800 mb-2">
                            To receive trip confirmations, driver info, and live updates on WhatsApp, you must opt-in <span className="font-bold">once</span>. Tap below — WhatsApp opens with a pre-filled message. <span className="font-bold">Just press Send (do not edit the text).</span>
                          </p>
                          <a
                            href="https://wa.me/14155238886?text=Join%20on-take"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="whatsapp-optin-btn-step1"
                          >
                            <Button type="button" className="bg-green-600 hover:bg-green-700 text-white rounded-full h-9 px-4 text-sm font-semibold">
                              <MessageCircle className="w-4 h-4 mr-2" />
                              Opt-in to WhatsApp Updates
                            </Button>
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Flight Info Summary (from Step 1) */}
                    {(formData.flight_number || flightInfo) && (
                      <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                        <Label className="text-sm font-bold text-blue-800 mb-2 block flex items-center gap-2">
                          <Plane className="w-4 h-4" />
                          Flight: {formData.flight_number || flightInfo?.flight_number}
                          {flightInfo && <span className="text-blue-600">({flightInfo.airline})</span>}
                        </Label>
                        {flightInfo?.arrival?.terminal && (
                          <p className="text-sm text-blue-700">Terminal: {flightInfo.arrival.terminal}</p>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <RequiredLabel>
                          Airline
                          <ErrorArrow show={showValidationErrors && !formData.airline} />
                        </RequiredLabel>
                        <Input
                          name="airline"
                          value={formData.airline}
                          onChange={handleInputChange}
                          placeholder="Auto-filled or enter manually"
                          data-testid="airline-input"
                          className={`h-12 rounded-xl bg-gray-50 border-[3px] border-red-500 focus:bg-white focus:border-red-600 ${showValidationErrors && !formData.airline ? 'animate-pulse' : ''}`}
                          required
                        />
                      </div>
                      <div>
                        <OptionalLabel>Arrival Terminal</OptionalLabel>
                        <Input
                          name="arrival_terminal"
                          value={formData.arrival_terminal}
                          onChange={handleInputChange}
                          placeholder="Auto-filled or enter manually"
                          data-testid="arrival-terminal-input"
                          className="h-12 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-green-500"
                        />
                      </div>
                    </div>

                    {/* Housing Phone */}
                    <div>
                      <OptionalLabel>
                        <Phone className="inline w-4 h-4 mr-1" />
                        Housing Unit Phone Number
                      </OptionalLabel>
                      <Input
                        name="housing_phone"
                        value={formData.housing_phone}
                        onChange={handleInputChange}
                        placeholder="Housing contact phone number"
                        data-testid="housing-phone-input"
                        className="h-12 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-green-500"
                      />
                    </div>

                    {/* Debt banner — appears when the primary passenger's phone matches an outstanding balance */}
                    {customerDebt && customerDebt.auto_apply_active && (
                      <div className="bg-red-50 border-2 border-red-400 rounded-2xl p-4 shadow-sm" data-testid="debt-alert-banner">
                        <div className="flex items-start gap-3">
                          <div className="bg-red-600 rounded-full p-2 flex-shrink-0">
                            <DollarSign className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-red-900 text-base mb-0.5">Outstanding Balance</h4>
                            <p className="text-sm text-red-900">
                              You have a prior balance of <span className="font-bold">${customerDebt.remaining_amount.toFixed(0)}</span>. A <span className="font-bold">${(customerDebt.surcharge_per_booking || 50).toFixed(0)} settlement payment</span> will be added to this booking to help pay it down.
                            </p>
                            <p className="text-xs text-red-800 mt-1">Remaining balance after this trip: <span className="font-bold">${Math.max(0, customerDebt.remaining_amount - (customerDebt.surcharge_per_booking || 50)).toFixed(0)}</span></p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Prominent Luggage Policy Banner + Selector (applies to ALL trips) */}
                    <div className={`bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border-2 ${showValidationErrors && (formData.total_suitcases === null || formData.total_suitcases === undefined) ? 'border-red-500 ring-2 ring-red-200' : 'border-amber-400'} rounded-2xl p-4 shadow-sm`} data-testid="luggage-policy-banner">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="bg-amber-500 rounded-full p-2 flex-shrink-0">
                          <Luggage className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-amber-900 text-base mb-0.5">Luggage Policy</h4>
                          <p className="text-sm text-amber-900">
                            <span className="font-bold">1st suitcase FREE</span> &nbsp;·&nbsp;
                            <span className="font-bold">$5 for each additional suitcase</span>
                          </p>
                          <p className="text-xs text-amber-800 mt-0.5">Applies to all trips (airport pickups and returns). Carry-on bags are always free.</p>
                        </div>
                      </div>
                      <Label className="text-sm font-semibold text-amber-900 mb-2 block">
                        Total checked suitcases <span className="text-red-600">*</span> <span className="font-normal text-xs">(required — 1 free per booking)</span>
                      </Label>
                      <Select
                        value={formData.total_suitcases === null || formData.total_suitcases === undefined ? "" : String(formData.total_suitcases)}
                        onValueChange={(value) => handleSelectChange("total_suitcases", parseInt(value))}
                      >
                        <SelectTrigger className={`h-12 rounded-xl bg-white border-2 ${showValidationErrors && (formData.total_suitcases === null || formData.total_suitcases === undefined) ? 'border-red-500 focus:border-red-600' : 'border-amber-300 focus:border-amber-500'}`} data-testid="total-suitcases-select">
                          <SelectValue placeholder="Select total suitcases (required)" />
                        </SelectTrigger>
                        <SelectContent>
                          {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(n => {
                            const extra = Math.max(0, n - 1);
                            const cost = extra * 5;
                            const label = cost === 0 ? "FREE" : `+$${cost}`;
                            return (
                              <SelectItem key={n} value={String(n)}>
                                {n} suitcase{n !== 1 ? 's' : ''} — {label}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {showValidationErrors && (formData.total_suitcases === null || formData.total_suitcases === undefined) && (
                        <p className="mt-2 text-xs font-semibold text-red-600" data-testid="luggage-required-error">Please select your total suitcase count to continue.</p>
                      )}
                      {formData.total_suitcases !== null && formData.total_suitcases !== undefined && formData.total_suitcases > 0 && (
                        <p className="mt-2 text-sm font-semibold text-amber-900" data-testid="luggage-fee-preview">
                          {formData.total_suitcases === 1 ? (
                            <>You have <span className="font-bold">1 suitcase</span> — included free</>
                          ) : (
                            <>You have <span className="font-bold">{formData.total_suitcases} suitcases</span> = +${Math.max(0, formData.total_suitcases - 1) * 5} luggage fee</>
                          )}
                        </p>
                      )}
                      {formData.total_suitcases === 0 && (
                        <p className="mt-2 text-sm font-semibold text-emerald-700" data-testid="luggage-fee-preview-zero">
                          Carry-on only — no luggage fee.
                        </p>
                      )}

                      {/* Carry-on count — 1st free, $5 each additional (headcount + fee) */}
                      <div className="mt-4 border-t border-amber-300 pt-3">
                        <Label className="text-sm font-semibold text-amber-900 mb-2 block">
                          Total carry-ons <span className="font-normal text-xs">(1st free · $5 each additional)</span>
                        </Label>
                        <Select
                          value={String(formData.total_carry_ons ?? 0)}
                          onValueChange={(value) => handleSelectChange("total_carry_ons", parseInt(value))}
                        >
                          <SelectTrigger className="h-11 rounded-xl bg-white border-2 border-amber-300 focus:border-amber-500" data-testid="total-carry-ons-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(n => {
                              const extra = Math.max(0, n - 1);
                              const cost = extra * 5;
                              const label = cost === 0 ? "FREE" : `+$${cost}`;
                              return (
                                <SelectItem key={n} value={String(n)}>
                                  {n} carry-on{n !== 1 ? 's' : ''} — {label}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        {formData.total_carry_ons > 1 && (
                          <p className="mt-1 text-xs font-semibold text-amber-900" data-testid="carry-on-fee-preview">
                            {formData.total_carry_ons} carry-ons = +${(formData.total_carry_ons - 1) * 5} extra luggage fee
                          </p>
                        )}
                      </div>

                      {/* Heavy-luggage nudge: if 3+ suitcases on a return trip AND shipping not yet enabled,
                          suggest T&M shipping so bags travel separately. */}
                      {formData.trip_type === "return" && formData.total_suitcases >= 3 && !formData.uses_shipping_company && (
                        <div className="mt-3 bg-blue-50 border-2 border-blue-300 rounded-xl p-3 flex items-start gap-3" data-testid="ship-bags-nudge">
                          <Package className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm text-blue-900">
                              <span className="font-bold">Traveling heavy?</span> With <span className="font-bold">{formData.total_suitcases} suitcases</span>, ship extras ahead with T&amp;M Courier — bags travel separately so the ride stays comfortable and your car fee drops to <span className="font-bold text-green-700">$70/person</span> if a second passenger books your slot.
                            </p>
                            <button
                              type="button"
                              onClick={() => handleSelectChange("uses_shipping_company", true)}
                              className="mt-2 text-xs font-bold text-blue-700 underline hover:text-blue-900"
                              data-testid="enable-shipping-from-nudge"
                            >
                              Ship my bags with T&amp;M →
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 4: Payment */}
                {step === 4 && (
                  <div className="space-y-6" data-testid="step-4">
                    {/* Redeem Referral Credits banner */}
                    {availableCredits > 0 && (
                      <div className="bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-4" data-testid="redeem-credits-banner">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-1 w-5 h-5 accent-emerald-600 cursor-pointer"
                            checked={!!formData.redeem_credits}
                            onChange={(e) => setFormData({ ...formData, redeem_credits: e.target.checked })}
                            data-testid="redeem-credits-checkbox"
                          />
                          <div className="flex-1">
                            <div className="font-bold text-emerald-800 flex items-center gap-1">
                              <DollarSign className="w-4 h-4" />
                              Redeem <span data-testid="available-credits-amount">${availableCredits.toFixed(0)}</span> referral credit
                            </div>
                            <p className="text-xs text-emerald-700 mt-1">
                              You&apos;ve earned this from friends who booked with your code. Apply it to today&apos;s trip and pay less at pickup.
                            </p>
                          </div>
                        </label>
                      </div>
                    )}

                    {/* Trip Total Breakdown */}
                    <div className="bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 border-2 border-emerald-300 rounded-2xl p-5 shadow-sm" data-testid="trip-total-breakdown">
                      <div className="flex items-center gap-2 mb-3">
                        <DollarSign className="w-5 h-5 text-emerald-700" />
                        <h4 className="font-bold text-emerald-900 text-base">Your Trip Total</h4>
                      </div>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-emerald-900">
                            {formData.trip_type === "return" ? "Return Trip" : "Airport Pickup"} · {formData.num_passengers} passenger{formData.num_passengers > 1 ? "s" : ""}
                            {formData.num_passengers >= 2 && formData.ride_type !== "private" && !pricing.is_solo && (
                              <span className="ml-2 text-[10px] font-bold bg-emerald-600 text-white px-1.5 py-0.5 rounded-full uppercase" data-testid="shared-rate-badge">
                                Shared Rate
                              </span>
                            )}
                          </span>
                          <span className="font-semibold text-emerald-900">${(pricing.trip_price || 0).toFixed(0)}</span>
                        </div>
                        {formData.num_passengers >= 2 && formData.ride_type !== "private" && !pricing.is_solo && (pricing.trip_price || 0) > 0 && (
                          <div className="flex justify-between text-[11px] text-emerald-700 -mt-1">
                            <span className="italic">${(pricing.trip_price / formData.num_passengers).toFixed(0)}/person × {formData.num_passengers} passenger{formData.num_passengers > 1 ? "s" : ""}</span>
                            <span></span>
                          </div>
                        )}
                        {/* Group discount / savings callout — shows the passenger how much
                            the group saves compared to booking one solo passenger. */}
                        {(() => {
                          if (formData.num_passengers < 2 || formData.ride_type === "private" || pricing.is_solo) return null;
                          const airport = airports.find(a => a.name === formData.pickup_location);
                          const zoneKey = airport?.pricing_zone || "ohare";
                          let soloPerPerson = 0;
                          const dropoffLc = (formData.dropoff_location || "").toLowerCase();
                          const isMadisonReturn = /madison|msn|dane county|dane regional/.test(dropoffLc);
                          if (formData.trip_type === "return") {
                            const ret = pricingConfig?.return || {};
                            soloPerPerson = isMadisonReturn ? (ret.madison_solo || 85) : (ret.solo || ret.private || 200);
                          } else {
                            const zc = pricingConfig?.[zoneKey] || pricingConfig?.ohare || {};
                            soloPerPerson = zc.solo || zc.private || 250;
                          }
                          const soloBaseline = soloPerPerson * formData.num_passengers;
                          const totalSavings = Math.max(0, soloBaseline - (pricing.trip_price || 0));
                          const perPersonSavings = Math.round(totalSavings / formData.num_passengers);
                          if (totalSavings <= 0) return null;
                          return (
                            <div className="flex justify-between items-center bg-emerald-100 border border-emerald-300 rounded-lg px-2.5 py-1.5 mt-1" data-testid="group-savings-line">
                              <span className="text-emerald-900 font-semibold text-xs">
                                💰 Group Discount ({formData.num_passengers} passengers sharing)
                              </span>
                              <span className="font-bold text-emerald-900 text-xs">−${totalSavings.toFixed(0)} <span className="text-[10px] font-normal">(saves ${perPersonSavings}/person)</span></span>
                            </div>
                          );
                        })()}
                        {pricing.luggage_fee > 0 && (
                          <div className="flex justify-between">
                            <span className="text-emerald-900">
                              {Math.max(0, (formData.total_suitcases || 0) - 1)} extra suitcase{Math.max(0, (formData.total_suitcases || 0) - 1) !== 1 ? "s" : ""} (${((pricingConfig?.return?.per_suitcase) || 5)} each)
                            </span>
                            <span className="font-semibold text-emerald-900">+${pricing.luggage_fee.toFixed(0)}</span>
                          </div>
                        )}
                        {formData.tip_amount > 0 && (
                          <div className="flex justify-between">
                            <span className="text-emerald-900">Driver tip ({formData.tip_percent || 0}%)</span>
                            <span className="font-semibold text-emerald-900">+${(formData.tip_amount || 0).toFixed(0)}</span>
                          </div>
                        )}
                        {pricing.shipping_savings > 0 && (
                          <div className="flex justify-between text-green-700">
                            <span>T&M Courier Savings</span>
                            <span className="font-semibold">−${pricing.shipping_savings.toFixed(0)}</span>
                          </div>
                        )}
                        {customerDebt && customerDebt.auto_apply_active && (
                          <div className="flex justify-between text-red-700 border-t border-red-200 pt-1.5 mt-1" data-testid="debt-surcharge-line">
                            <span className="font-semibold">Prior balance settlement</span>
                            <span className="font-bold">+${(customerDebt.surcharge_per_booking || 50).toFixed(0)}</span>
                          </div>
                        )}
                        <div className="border-t border-emerald-300 mt-2 pt-2 flex justify-between text-base font-bold text-emerald-900">
                          <span>Trip Total{customerDebt && customerDebt.auto_apply_active ? " (incl. settlement)" : ""}</span>
                          <span data-testid="trip-total-amount">
                            ${(
                              (pricing.trip_price || 0)
                              + (pricing.luggage_fee || 0)
                              + (formData.tip_amount || 0)
                              - (pricing.shipping_savings || 0)
                              + (customerDebt && customerDebt.auto_apply_active ? (customerDebt.surcharge_per_booking || 50) : 0)
                            ).toFixed(0)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm pt-1">
                          <span className="text-emerald-800">
                            {formData.pay_full_trip ? "You'll pay now" : `Deposit due now (${formData.num_passengers} × $${pricing.deposit_per_person || 20})`}
                          </span>
                          <span className="font-bold text-emerald-900" data-testid="due-now-amount">
                            ${(
                              (formData.pay_full_trip
                                ? ((pricing.trip_price || 0) + (pricing.luggage_fee || 0) + (formData.tip_amount || 0) - (pricing.shipping_savings || 0))
                                : ((pricing.deposit_per_person || 20) * formData.num_passengers))
                              + (customerDebt && customerDebt.auto_apply_active ? (customerDebt.surcharge_per_booking || 50) : 0)
                            ).toFixed(0)}
                          </span>
                        </div>
                        {!formData.pay_full_trip && (
                          <p className="text-xs text-emerald-800 italic mt-1">
                            Remaining ${((pricing.trip_price || 0) + (pricing.luggage_fee || 0) - ((pricing.deposit_per_person || 20) * formData.num_passengers)).toFixed(0)} due to driver at pickup
                          </p>
                        )}
                      </div>
                    </div>
                    {(() => {
                      const fullPaymentEligible = ["zelle", "cashapp", "cash"].includes(formData.payment_method);
                      // Auto-revert to deposit if user selected an ineligible method
                      if (!fullPaymentEligible && formData.pay_full_trip) {
                        // Use a microtask to avoid setState during render
                        Promise.resolve().then(() => {
                          setFormData(prev => prev.pay_full_trip ? { ...prev, pay_full_trip: false } : prev);
                        });
                      }
                      return null;
                    })()}
                    {/* Payment Amount Option */}
                    <div className="space-y-3">
                      <Label className="text-base font-semibold block">How much would you like to pay now?</Label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div
                          className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                            !formData.pay_full_trip ? "border-green-600 bg-green-50 shadow-sm" : "border-muted hover:border-green-300"
                          }`}
                          onClick={() => setFormData(prev => ({ ...prev, pay_full_trip: false }))}
                          data-testid="pay-deposit-option"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-bold text-lg">${(pricing.deposit_per_person || 20) * formData.num_passengers} Deposit</span>
                            <Badge className="bg-green-100 text-green-700">Most Popular</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">${pricing.deposit_per_person || 20}/person deposit now. Pay remaining ${((pricing.trip_price || 0) - (pricing.deposit_per_person || 20) * formData.num_passengers).toFixed(0)} on trip day.</p>
                        </div>
                        {(() => {
                          const fullPaymentEligible = ["zelle", "cashapp", "cash"].includes(formData.payment_method);
                          return (
                            <div
                              className={`p-4 rounded-2xl border-2 transition-all relative ${
                                !fullPaymentEligible
                                  ? "border-muted bg-gray-50 opacity-60 cursor-not-allowed"
                                  : formData.pay_full_trip
                                    ? "border-green-600 bg-green-50 shadow-sm cursor-pointer"
                                    : "border-muted hover:border-green-300 cursor-pointer"
                              }`}
                              onClick={() => fullPaymentEligible && setFormData(prev => ({ ...prev, pay_full_trip: true }))}
                              data-testid="pay-full-option"
                              title={!fullPaymentEligible ? "Full payment is only available with Zelle, CashApp, or Cash" : ""}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-bold text-lg">${(pricing.trip_price + (pricing.luggage_fee || 0)).toFixed(0)} Full Trip</span>
                                <Badge className={fullPaymentEligible ? "bg-blue-100 text-blue-700" : "bg-gray-200 text-gray-600"}>
                                  {fullPaymentEligible ? "No Balance Due" : "Zelle / CashApp / Cash only"}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {fullPaymentEligible
                                  ? "Pay the full trip price now. Nothing due on trip day!"
                                  : "Switch payment method below to Zelle, CashApp, or Cash to enable full payment."}
                              </p>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Tip Jar */}
                      {formData.pay_full_trip && (
                        <div className="bg-gradient-to-r from-pink-50 to-rose-50 border-2 border-pink-200 rounded-2xl p-4 mt-3" data-testid="tip-jar">
                          <div className="flex items-center gap-2 mb-2">
                            <Heart className="w-4 h-4 text-rose-500 fill-rose-500" />
                            <p className="font-bold text-rose-900 text-sm">Add a Tip for Your Driver?</p>
                          </div>
                          <p className="text-xs text-rose-800 mb-3">
                            100% of every tip goes directly to your driver. They appreciate it!
                          </p>
                          <div className="grid grid-cols-4 gap-2">
                            {[0, 10, 15, 20].map(pct => {
                              const tipDollars = Math.round(((pricing.trip_price || 0) + (pricing.luggage_fee || 0)) * pct / 100);
                              const isSelected = formData.tip_percent === pct;
                              return (
                                <button
                                  key={pct}
                                  type="button"
                                  onClick={() => setFormData(prev => ({ ...prev, tip_percent: pct, tip_amount: tipDollars }))}
                                  className={`p-2 rounded-xl border-2 transition-all text-center ${
                                    isSelected ? "border-rose-500 bg-rose-100" : "border-rose-200 bg-white hover:border-rose-300"
                                  }`}
                                  data-testid={`tip-${pct}`}
                                >
                                  <p className="font-bold text-sm text-rose-900">{pct === 0 ? "No Tip" : `${pct}%`}</p>
                                  {pct > 0 && <p className="text-[10px] text-rose-700">+${tipDollars}</p>}
                                </button>
                              );
                            })}
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <Label className="text-xs text-rose-800 whitespace-nowrap">Custom $</Label>
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              value={formData.tip_percent === -1 ? formData.tip_amount : ""}
                              onChange={(e) => setFormData(prev => ({ ...prev, tip_percent: -1, tip_amount: parseInt(e.target.value) || 0 }))}
                              placeholder="Enter custom amount"
                              className="h-8 text-sm"
                              data-testid="tip-custom-input"
                            />
                          </div>
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground">Note: Shared ride pricing ($120/person) applies when 2+ passengers travel on the same day from the same airport. Solo riders may be charged up to $250.</p>
                      {pricing.is_solo && formData.trip_type === "arrival" && (
                        <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 mt-2" data-testid="solo-rider-notice">
                          <p className="text-xs text-amber-900">
                            <span className="font-bold">Solo Rider Notice:</span> You're currently the only passenger booked for {formData.travel_date} from {formData.pickup_location}. Solo rate of <span className="font-bold">${(pricing.trip_price || 0).toFixed(0)}</span> applies. If another passenger books for the same day/airport, your price automatically drops to ${((pricingConfig?.[airports.find(a => a.name === formData.pickup_location)?.pricing_zone || "ohare"]?.shared) || 120).toFixed(0)} (shared rate).
                          </p>
                        </div>
                      )}
                      {formData.num_passengers >= 2 && formData.ride_type !== "private" && !pricing.is_solo && (
                        <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-3 mt-2" data-testid="shared-rate-applied-notice">
                          <p className="text-xs text-emerald-900">
                            <span className="font-bold">✅ Shared Rate Applied:</span> Your group of {formData.num_passengers} passengers qualifies for the shared-ride rate.
                            You pay <span className="font-bold">${(pricing.trip_price / formData.num_passengers).toFixed(0)}/person</span> —
                            total <span className="font-bold">${(pricing.trip_price || 0).toFixed(0)}</span> for the group.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* WhatsApp Opt-in Reminder before submit */}
                    <div className="bg-green-50 border border-green-300 rounded-xl p-3 flex items-center gap-3" data-testid="whatsapp-optin-reminder">
                      <MessageCircle className="w-5 h-5 text-green-700 flex-shrink-0" />
                      <div className="flex-1 text-xs text-green-900">
                        <span className="font-bold">Don't forget:</span> Opt-in to WhatsApp now to receive your confirmation. Tap, then press <span className="font-bold">Send</span> on the pre-filled message.
                      </div>
                      <a
                        href="https://wa.me/14155238886?text=Join%20on-take"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="whatsapp-optin-btn-step4"
                      >
                        <Button type="button" size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-full text-xs h-8 px-3 whitespace-nowrap">
                          Opt-in Now
                        </Button>
                      </a>
                    </div>

                    <Label className="text-base font-semibold mb-4 block">Select Payment Method</Label>
                    <RadioGroup
                      value={formData.payment_method}
                      onValueChange={(value) => handleSelectChange("payment_method", value)}
                      className="grid grid-cols-2 md:grid-cols-3 gap-4"
                    >
                      <Label 
                        htmlFor="stripe"
                        className={`flex flex-col items-center p-6 rounded-2xl border-2 cursor-pointer transition-colors ${
                          formData.payment_method === "stripe" ? "border-green-600 bg-green-50" : "border-muted hover:border-green-300"
                        }`}
                      >
                        <RadioGroupItem value="stripe" id="stripe" className="sr-only" />
                        <CreditCard className="w-8 h-8 mb-2 text-blue-600" />
                        <span className="font-bold">Credit Card</span>
                        <span className="text-xs text-muted-foreground">Secure payment</span>
                      </Label>
                      <Label 
                        htmlFor="apple_pay"
                        className={`flex flex-col items-center p-6 rounded-2xl border-2 cursor-pointer transition-colors ${
                          formData.payment_method === "apple_pay" ? "border-green-600 bg-green-50" : "border-muted hover:border-green-300"
                        }`}
                      >
                        <RadioGroupItem value="apple_pay" id="apple_pay" className="sr-only" />
                        <div className="w-8 h-8 mb-2 bg-black rounded-lg flex items-center justify-center text-white font-bold text-sm"></div>
                        <span className="font-bold">Apple Pay</span>
                        <span className="text-xs text-muted-foreground">Quick & easy</span>
                      </Label>
                      <Label 
                        htmlFor="paypal"
                        className={`flex flex-col items-center p-6 rounded-2xl border-2 cursor-pointer transition-colors ${
                          formData.payment_method === "paypal" ? "border-green-600 bg-green-50" : "border-muted hover:border-green-300"
                        }`}
                      >
                        <RadioGroupItem value="paypal" id="paypal" className="sr-only" />
                        <div className="w-8 h-8 mb-2 bg-blue-700 rounded-lg flex items-center justify-center text-white font-bold text-xs">PP</div>
                        <span className="font-bold">PayPal</span>
                        <span className="text-xs text-muted-foreground">Instyle Limousine inc</span>
                      </Label>
                      <Label 
                        htmlFor="cashapp"
                        className={`flex flex-col items-center p-6 rounded-2xl border-2 cursor-pointer transition-colors ${
                          formData.payment_method === "cashapp" ? "border-green-600 bg-green-50" : "border-muted hover:border-green-300"
                        }`}
                      >
                        <RadioGroupItem value="cashapp" id="cashapp" className="sr-only" />
                        <div className="w-8 h-8 mb-2 bg-green-500 rounded-lg flex items-center justify-center text-white font-bold text-xs">$</div>
                        <span className="font-bold">CashApp</span>
                        <span className="text-xs text-muted-foreground">$Vybez2</span>
                      </Label>
                      <Label 
                        htmlFor="zelle"
                        className={`flex flex-col items-center p-6 rounded-2xl border-2 cursor-pointer transition-colors ${
                          formData.payment_method === "zelle" ? "border-green-600 bg-green-50" : "border-muted hover:border-green-300"
                        }`}
                      >
                        <RadioGroupItem value="zelle" id="zelle" className="sr-only" />
                        <div className="w-8 h-8 mb-2 bg-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-xs">Z</div>
                        <span className="font-bold">Zelle</span>
                        <span className="text-xs text-muted-foreground">MICHENO LAWRENCE</span>
                      </Label>
                      <Label 
                        htmlFor="bank_transfer"
                        className={`flex flex-col items-center p-6 rounded-2xl border-2 cursor-pointer transition-colors ${
                          formData.payment_method === "bank_transfer" ? "border-green-600 bg-green-50" : "border-muted hover:border-green-300"
                        }`}
                      >
                        <RadioGroupItem value="bank_transfer" id="bank_transfer" className="sr-only" />
                        <div className="w-8 h-8 mb-2 bg-yellow-500 rounded-lg flex items-center justify-center text-white font-bold text-xs">B</div>
                        <span className="font-bold">Bank Transfer</span>
                        <span className="text-xs text-muted-foreground">JMD Scotiabank</span>
                      </Label>
                    </RadioGroup>

                    {/* Payment QR Codes and Info */}
                    {(formData.payment_method === "cashapp" || formData.payment_method === "zelle" || formData.payment_method === "paypal" || formData.payment_method === "bank_transfer") && (
                      <div className="mt-6 p-6 bg-gray-50 rounded-2xl border">
                        <h4 className="font-bold text-center mb-4">
                          {formData.payment_method === "cashapp" && "Pay with CashApp"}
                          {formData.payment_method === "zelle" && "Pay with Zelle"}
                          {formData.payment_method === "paypal" && "Pay with PayPal"}
                        </h4>
                        
                        {formData.payment_method === "cashapp" && (
                          <div className="text-center">
                            <div className="inline-block bg-green-500 text-white px-6 py-3 rounded-2xl mb-4">
                              <p className="text-2xl font-bold">$Vybez2</p>
                            </div>
                            <p className="text-sm text-muted-foreground">Send payment to $Vybez2 on Cash App</p>
                            <img 
                              src="https://customer-assets.emergentagent.com/job_ccdc1713-4372-47e5-bc41-50a444a2aa79/artifacts/6qqh6dk6_Screenshot_20260307_181032_WhatsApp.jpg" 
                              alt="CashApp QR Code" 
                              className="w-48 h-auto mx-auto mt-4 rounded-lg shadow-md"
                            />
                          </div>
                        )}
                        
                        {formData.payment_method === "zelle" && (
                          <div className="text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500 rounded-full text-white font-bold text-xl mb-3">
                              ML
                            </div>
                            <p className="font-bold text-lg">MICHENO LAWRENCE</p>
                            <p className="text-muted-foreground">g***e@gmail.com</p>
                            <img 
                              src="https://customer-assets.emergentagent.com/job_ccdc1713-4372-47e5-bc41-50a444a2aa79/artifacts/0fc0xq1t_IMG-20260307-WA0000.jpg" 
                              alt="Zelle QR Code" 
                              className="w-48 h-auto mx-auto mt-4 rounded-lg shadow-md"
                            />
                            <p className="text-xs text-muted-foreground mt-3">Scan QR code in your banking app to pay</p>
                          </div>
                        )}
                        
                        {formData.payment_method === "paypal" && (
                          <div className="text-center">
                            <p className="font-bold text-lg mb-2">Instyle Limousine inc</p>
                            <img 
                              src="https://customer-assets.emergentagent.com/job_ccdc1713-4372-47e5-bc41-50a444a2aa79/artifacts/030fi1oc_IMG-20260307-WA0001.jpg" 
                              alt="PayPal QR Code" 
                              className="w-48 h-auto mx-auto mt-4 rounded-lg shadow-md"
                            />
                            <p className="text-xs text-muted-foreground mt-3">Scan to pay with PayPal</p>
                          </div>
                        )}

                        {formData.payment_method === "bank_transfer" && (
                          <div className="text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-500 rounded-full text-white font-bold text-xl mb-3">
                              JMD
                            </div>
                            <p className="font-bold text-lg mb-4">Scotiabank - Jamaica</p>
                            <div className="bg-white rounded-xl p-4 border border-gray-200 text-left max-w-sm mx-auto">
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                <div className="text-muted-foreground">Account Holder:</div>
                                <div className="font-semibold">LISA HANSON</div>
                                <div className="text-muted-foreground">Bank Name:</div>
                                <div className="font-semibold">Scotiabank</div>
                                <div className="text-muted-foreground">Branch:</div>
                                <div className="font-semibold">May Pen</div>
                                <div className="text-muted-foreground">Account Number:</div>
                                <div className="font-semibold">000850348</div>
                                <div className="text-muted-foreground">Account Type:</div>
                                <div className="font-semibold">SAVINGS</div>
                                <div className="text-muted-foreground">Currency:</div>
                                <div className="font-semibold">JMD</div>
                              </div>
                            </div>
                            <p className="text-xs text-muted-foreground mt-3">Deposit: $3,200 JMD per person</p>
                            <p className="text-xs text-red-500 mt-1 font-medium">Please upload screenshot of payment receipt after booking</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Payment Screenshot Upload */}
                    {(formData.payment_method === "cashapp" || formData.payment_method === "zelle" || formData.payment_method === "paypal" || formData.payment_method === "bank_transfer") && (
                      <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200">
                        <Label className="text-sm font-semibold mb-2 flex items-center gap-2">
                          <Upload className="w-4 h-4 text-blue-600" />
                          Upload Payment Screenshot
                        </Label>
                        <p className="text-xs text-muted-foreground mb-3">
                          After making your payment, upload a screenshot as proof of payment.
                        </p>
                        
                        {!paymentScreenshotPreview ? (
                          <div className="relative">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handlePaymentScreenshotChange}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                              data-testid="payment-screenshot-input"
                            />
                            <div className="border-2 border-dashed border-blue-300 rounded-xl p-6 text-center hover:border-blue-500 hover:bg-blue-50 transition-colors">
                              <Image className="w-10 h-10 mx-auto mb-2 text-blue-400" />
                              <p className="text-sm font-medium text-blue-600">Click or drag to upload</p>
                              <p className="text-xs text-muted-foreground mt-1">PNG, JPG up to 5MB</p>
                            </div>
                          </div>
                        ) : (
                          <div className="relative">
                            <img 
                              src={paymentScreenshotPreview} 
                              alt="Payment Screenshot" 
                              className="w-full max-h-48 object-contain rounded-lg border border-gray-200"
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              onClick={removePaymentScreenshot}
                              className="absolute top-2 right-2"
                            >
                              Remove
                            </Button>
                            <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                              <Check className="w-3 h-3" /> Screenshot uploaded successfully
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Step 5: Confirmation */}
                {step === 5 && (
                  <div className="space-y-6" data-testid="step-5">
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Review Your Booking
                      </h3>
                      <p className="text-muted-foreground">Please confirm all details are correct</p>
                    </div>

                    <div className="ticket-stub bg-gradient-to-r from-green-50 to-yellow-50 rounded-2xl p-6 border border-dashed border-green-300">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Trip Type</span>
                          <p className="font-semibold capitalize">{formData.trip_type}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Date & Time</span>
                          <p className="font-semibold">{formData.travel_date} at {formData.travel_time}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">From</span>
                          <p className="font-semibold">{formData.trip_type === "arrival" ? formData.pickup_location : (formData.housing_unit || formData.housing_address)}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">To</span>
                          <p className="font-semibold">{formData.trip_type === "arrival" ? (formData.housing_unit || formData.housing_address) : formData.pickup_location}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Flight</span>
                          <p className="font-semibold">{formData.airline} - {formData.flight_number}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Arrival Terminal</span>
                          <p className="font-semibold">{formData.arrival_terminal || "Not specified"}</p>
                        </div>
                        {formData.email && (
                          <div>
                            <span className="text-muted-foreground">Email</span>
                            <p className="font-semibold">{formData.email}</p>
                          </div>
                        )}
                        <div>
                          <span className="text-muted-foreground">Payment</span>
                          <p className="font-semibold capitalize">{formData.payment_method}</p>
                        </div>
                        {formData.housing_phone && (
                          <div>
                            <span className="text-muted-foreground">Housing Phone</span>
                            <p className="font-semibold">{formData.housing_phone}</p>
                          </div>
                        )}
                        {formData.trip_type === "return" && formData.total_suitcases > 0 && (
                          <div>
                            <span className="text-muted-foreground">Suitcases</span>
                            <p className="font-semibold">{formData.total_suitcases} (1st free, {Math.max(0, formData.total_suitcases - 1)} extra)</p>
                          </div>
                        )}
                        {formData.trip_type === "return" && formData.departure_time && (
                          <div>
                            <span className="text-muted-foreground">Departure Time</span>
                            <p className="font-semibold text-orange-600">{formData.departure_time}</p>
                          </div>
                        )}
                      </div>

                      {/* All Passengers List */}
                      <div className="mt-4 pt-4 border-t border-green-200">
                        <span className="text-muted-foreground text-sm">Passengers ({passengers.length})</span>
                        <div className="mt-2 space-y-2">
                          {passengers.map((p, idx) => (
                            <div key={idx} className="flex justify-between text-sm bg-white/50 p-2 rounded-lg">
                              <span className="font-semibold">{p.name || `Passenger ${idx + 1}`}</span>
                              <span className="text-muted-foreground">{p.whatsapp || "No WhatsApp"}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Payment Summary */}
                    <div className="bg-white rounded-2xl p-6 border-2 border-green-200 shadow-sm" data-testid="confirmation-payment-summary">
                      <h4 className="font-bold text-lg mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <CreditCard className="w-5 h-5 text-green-600" />
                        Payment Summary
                      </h4>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Trip Price ({formData.num_passengers} passenger{formData.num_passengers > 1 ? 's' : ''})</span>
                          <span className="font-semibold">${pricing.trip_price.toFixed(2)}</span>
                        </div>
                        {formData.num_passengers > 1 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground ml-4">({`$${(pricing.trip_price / formData.num_passengers).toFixed(2)}/person`})</span>
                            <span></span>
                          </div>
                        )}
                        {pricing.luggage_fee > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Extra Luggage Fee ({Math.max(0, formData.total_suitcases - 1)} suitcase{Math.max(0, formData.total_suitcases - 1) !== 1 ? 's' : ''})</span>
                            <span className="font-semibold">${pricing.luggage_fee.toFixed(2)}</span>
                          </div>
                        )}
                        {pricing.shipping_savings > 0 && (
                          <div className="flex justify-between text-green-600">
                            <span>T&M Courier Savings</span>
                            <span className="font-semibold">-${pricing.shipping_savings.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="border-t pt-3 mt-3">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Deposit ({`$${pricing.deposit_per_person || 20}/person × ${formData.num_passengers}`})</span>
                            <span className="font-semibold">${pricing.deposit.toFixed(2)}</span>
                          </div>
                        </div>
                        <div className="bg-green-50 rounded-xl p-4 mt-2">
                          <div className="flex justify-between items-center">
                            <span className="font-bold text-base">Paying Now</span>
                            <span className="font-extrabold text-xl text-green-600">${pricing.total_due_now.toFixed(2)}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">via {formData.payment_method === 'stripe' ? 'Credit Card' : formData.payment_method === 'apple_pay' ? 'Apple Pay' : formData.payment_method === 'bank_transfer' ? 'Bank Transfer' : formData.payment_method.charAt(0).toUpperCase() + formData.payment_method.slice(1)}</p>
                        </div>
                        <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-amber-800">Balance Due on Trip Day</span>
                            <span className="font-bold text-lg text-amber-700">${(pricing.trip_price + (pricing.luggage_fee || 0) - pricing.deposit).toFixed(2)}</span>
                          </div>
                          {formData.num_passengers > 1 && (
                            <p className="text-xs text-amber-600 mt-1">${((pricing.trip_price + (pricing.luggage_fee || 0) - pricing.deposit) / formData.num_passengers).toFixed(2)}/person</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* WhatsApp Opt-in */}
                {step === 5 && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-4" data-testid="whatsapp-optin-booking">
                    <a
                      href="http://wa.me/+14155238886?text=join%20on-take"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 group"
                      data-testid="whatsapp-optin-booking-link"
                    >
                      <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0 group-hover:bg-green-700 transition-colors">
                        <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.75.75 0 00.917.918l4.458-1.495A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.331 0-4.512-.637-6.39-1.743l-.458-.275-3.163 1.06 1.06-3.163-.275-.458A9.953 9.953 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-green-800 text-sm">Tap to receive WhatsApp confirmations</p>
                        <p className="text-xs text-green-600">Opens WhatsApp — just hit Send to subscribe to trip updates</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-green-500 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                )}

                {/* Navigation Buttons */}
                <BookingNavigation step={step} loading={loading} pricing={pricing} onBack={handleBack} onNext={handleNext} onSubmit={handleSubmit} onStartOver={handleStartOver} />
              </CardContent>
            </Card>
          </div>

          {/* Summary Sidebar */}
          <div className="md:col-span-1">
            <BookingSummary pricing={pricing} formData={formData} customerDebt={customerDebt} />
            <PricingGuidelines pricingConfig={pricingConfig} />
          </div>
        </div>
      </div>
    </div>
  );
}
